import {
  createEffect,
  createEvent,
  createStore,
  sample,
} from 'effector';
import { debounce } from 'patronum/debounce';
import axios, { type CancelToken } from 'axios';
import {
  dictionaryCreate,
  dictionaryGet,
  dictionaryGetList,
  dictionaryUpdate,
  DictionaryFields,
  DictionaryGetListResponse,
  DictionaryGetListRow,
} from '../../../../../../../apiRPC/dictionary';

import { createEffectJWT } from '../../../../../../../model/JWT';
import { errorHandlerFx } from '../../../../../../../model/errorHandler';
import { AGENCY_NAME_OPTIONS } from '../agencyNameOptions';
import { CLIENT_NAME_OPTIONS } from '../clientNameOptions';

type DictionaryConfig = {
  name: string;
  initialWords: string[];
};

type DictionaryMeta = {
  uuid: string | null;
  total: number;
  limit: number;
  offset: number;
  search: string;
  loading: boolean;
};

const DEFAULT_LIMIT = 100;

type EnsureDictionaryResult = {
  uuid: string;
};

type LoadWordsParams = {
  uuid: string;
  offset: number;
  search: string;
};

type LoadWordsRequest = {
  params: LoadWordsParams;
  cancelToken?: CancelToken;
};

type LoadWordsResult = {
  fields: DictionaryFields;
  offset: number;
};

type DeleteWordParams = {
  uuid: string;
  word: string;
};

type AddWordParams = {
  uuid: string;
  word: string;
};

type CheckWordParams = {
  uuid: string;
  word: string;
};

type CheckWordRequest = {
  params: CheckWordParams;
  cancelToken?: CancelToken;
};

const findDictionaryByName = (
  response: DictionaryGetListResponse,
  name: string,
): DictionaryGetListRow | null =>
  response.rows.find((row: DictionaryGetListRow) => row.name === name) ?? null;

const initialMeta: DictionaryMeta = {
  uuid: null,
  total: 0,
  limit: DEFAULT_LIMIT,
  offset: 0,
  search: '',
  loading: false,
};

const createDictionaryModel = (config: DictionaryConfig) => {
  const initDictionary = createEvent();
  const loadMoreRequested = createEvent();
  const searchChanged = createEvent<string>();
  const reset = createEvent();
  const seedKnownWords = createEvent<string[]>();

  const $meta = createStore<DictionaryMeta>({ ...initialMeta }).reset(reset);

  const $words = createStore<string[]>([]).reset(reset);

  const $knownWords = createStore<Record<string, boolean>>({}).reset(reset);

  const setLastSearchRequested = createEvent<string>();
  const $lastSearchRequested = createStore<string | null>(null)
    .on(setLastSearchRequested, (_, s) => s)
    .reset(reset);

  const setLastSearchTime = createEvent<number>();
  const $lastSearchTime = createStore<number>(0)
    .on(setLastSearchTime, (_, t) => t)
    .reset(reset);

  const SEARCH_DEBOUNCE_MS = 300;

  const ensureDictionaryBaseFx = createEffectJWT<
    void,
    EnsureDictionaryResult,
    unknown
  >(async (_: void, cancelToken?: CancelToken) => {
    const list = await dictionaryGetList(
      {
        filter: {
          limit: 1000,
          offset: 0,
        },
      },
      cancelToken,
    );

    const existing = findDictionaryByName(list, config.name);
    if (existing) {
      return { uuid: existing.uuid };
    }

    const created = await dictionaryCreate(
      {
        fields: {
          name: config.name,
          words: Array.from(new Set(config.initialWords)).filter(Boolean),
        },
      },
      cancelToken,
    );

    return { uuid: created.fields.uuid };
  });

  const ensureDictionaryFx = errorHandlerFx(ensureDictionaryBaseFx);

  let loadWordsCancel: (() => void) | null = null;
  let checkWordCancel: (() => void) | null = null;

  const loadWordsBaseFx = createEffectJWT<
    LoadWordsRequest,
    LoadWordsResult,
    unknown
  >(async (request: LoadWordsRequest) => {
    const { params, cancelToken } = request;
    const { uuid, offset, search } = params;

    const response = await dictionaryGet(
      {
        uuid,
        wordsFilter: {
          limit: DEFAULT_LIMIT,
          offset,
          search: search || undefined,
        },
      },
      cancelToken,
    );

    return {
      fields: response.fields,
      offset,
    };
  });

  const loadWordsFx = errorHandlerFx(loadWordsBaseFx);

  const deleteWordBaseFx = createEffectJWT<
    DeleteWordParams,
    { word: string },
    unknown
  >(async (params: DeleteWordParams, cancelToken?: CancelToken) => {
    await dictionaryUpdate(
      {
        uuid: params.uuid,
        fields: {
          words: {
            remove: [params.word],
          },
        },
      },
      cancelToken,
    );

    return { word: params.word };
  });

  const deleteWordFx = errorHandlerFx(deleteWordBaseFx);

  const addWordBaseFx = createEffectJWT<
    AddWordParams,
    { word: string },
    unknown
  >(async (params: AddWordParams, cancelToken?: CancelToken) => {
    const word = params.word.trim();
    if (!word) {
      return { word };
    }

    await dictionaryUpdate(
      {
        uuid: params.uuid,
        fields: {
          words: {
            add: [word],
          },
        },
      },
      cancelToken,
    );

    return { word };
  });

  const addWordFx = errorHandlerFx(addWordBaseFx);

  const addWordRequested = createEvent<string>();

  const checkWordBaseFx = createEffectJWT<
    CheckWordRequest,
    { word: string; exists: boolean },
    unknown
  >(async (request: CheckWordRequest) => {
    const { params, cancelToken } = request;
    const word = params.word.trim();
    if (!word) {
      return { word, exists: false };
    }

    const response = await dictionaryGet(
      {
        uuid: params.uuid,
        wordsFilter: {
          limit: 20,
          offset: 0,
          search: word,
        },
      },
      cancelToken,
    );

    // Бекенд ищет по вхождению; считаем валидным только точное совпадение
    const exists = response.fields.words.list.some((w) => w.trim() === word);

    return { word, exists };
  });

  const checkWordFx = errorHandlerFx(checkWordBaseFx);

  const checkWordRequested = createEvent<string>();

  $meta
    .on(ensureDictionaryFx.pending, (meta, pending) => ({
      ...meta,
      loading: pending,
    }))
    .on(loadWordsFx.pending, (meta, pending) => ({
      ...meta,
      loading: pending,
    }))
    .on(ensureDictionaryFx.doneData, (meta, { uuid }) => ({
      ...meta,
      uuid,
    }))
    .on(loadWordsFx.doneData, (meta, { fields, offset }) => {
      const loadedCount = fields.words.list.length;
      const nextOffset = offset + loadedCount;

      return {
        ...meta,
        total: fields.words.total,
        offset: nextOffset,
      };
    })
    .on(searchChanged, (meta, search) => ({
      ...meta,
      search,
      offset: 0,
    }));

  $words
    .on(loadWordsFx.doneData, (words, { fields, offset }) => {
      if (offset === 0) {
        return fields.words.list;
      }

      const next = [...words, ...fields.words.list];
      return Array.from(new Set(next));
    })
    .on(deleteWordFx.doneData, (words, { word }) =>
      words.filter((item) => item !== word),
    )
    .on(addWordFx.doneData, (words, { word }) => {
      const trimmed = word.trim();
      if (!trimmed) return words;
      if (words.includes(trimmed)) return words;
      return [...words, trimmed];
    })
    .on(searchChanged, () => []);

  $knownWords
    .on(loadWordsFx.doneData, (known, { fields }) => {
      const next = { ...known };
      fields.words.list.forEach((w) => {
        const trimmed = w.trim();
        if (!trimmed) return;
        next[trimmed] = true;
      });
      return next;
    })
    .on(addWordFx.doneData, (known, { word }) => {
      const trimmed = word.trim();
      if (!trimmed) return known;
      return { ...known, [trimmed]: true };
    })
    .on(deleteWordFx.doneData, (known, { word }) => {
      const trimmed = word.trim();
      if (!trimmed) return known;
      return { ...known, [trimmed]: false };
    })
    .on(checkWordFx.doneData, (known, { word, exists }) => {
      const trimmed = word.trim();
      if (!trimmed) return known;
      return { ...known, [trimmed]: exists };
    })
    .on(seedKnownWords, (known, words) => {
      if (!Array.isArray(words) || words.length === 0) return known;
      const next = { ...known };
      words.forEach((w) => {
        const trimmed = typeof w === 'string' ? w.trim() : '';
        if (trimmed) next[trimmed] = true;
      });
      return next;
    });

   const $knownWordsOnlyDictionary = createStore<Record<string, boolean>>({})
    .on(loadWordsFx.doneData, (known, { fields }) => {
      const next = { ...known };
      fields.words.list.forEach((w) => {
        const trimmed = w.trim();
        if (!trimmed) return
        next[trimmed] = true;
      });
      return next;
    })
    .on(addWordFx.doneData, (known, { word }) => {
      const trimmed = word.trim();
      if (!trimmed) return known;
      return { ...known, [trimmed]: true };
    })
    .on(deleteWordFx.doneData, (known, { word }) => {
      const trimmed = word.trim();
      if (!trimmed) return known;
      return { ...known, [trimmed]: false };
    })
    .on(checkWordFx.doneData, (known, { word, exists }) => {
      const trimmed = word.trim();
      if (!trimmed) return known;
      return { ...known, [trimmed]: exists };
    }).reset(reset);


  sample({
    clock: initDictionary,
    target: ensureDictionaryFx,
  });

  sample({
    clock: addWordRequested,
    source: $meta,
    filter: (meta: DictionaryMeta, word: string) =>
      Boolean(meta.uuid) && Boolean(word && word.trim()),
    fn: (meta: DictionaryMeta, word: string) => ({
      uuid: meta.uuid as string,
      word,
    }),
    target: addWordFx,
  });


  const checkWordRequestedDebounced = debounce({
    source: checkWordRequested,
    timeout: SEARCH_DEBOUNCE_MS,
  });

  sample({
    clock: debounce({
      source: checkWordRequested,
      timeout: SEARCH_DEBOUNCE_MS,
    }),
    source: $meta,
    filter: (meta: DictionaryMeta, word: string) =>
      Boolean(meta.uuid) && Boolean(word && word.trim()),
    fn: (meta: DictionaryMeta, word: string) => {
      checkWordCancel?.();
      const source = axios.CancelToken.source();
      checkWordCancel = source.cancel;
      return {
        params: { uuid: meta.uuid as string, word },
        cancelToken: source.token,
      };
    },
    target: checkWordFx,
  });

  sample({
    clock: ensureDictionaryFx.doneData,
    source: $meta,
    fn: (meta: DictionaryMeta, { uuid }: EnsureDictionaryResult) => {
      loadWordsCancel?.();
      const source = axios.CancelToken.source();
      loadWordsCancel = source.cancel;
      return {
        params: { uuid, offset: 0, search: meta.search },
        cancelToken: source.token,
      };
    },
    target: loadWordsFx,
  });

  sample({
    clock: loadMoreRequested,
    source: $meta,
    filter: (meta: DictionaryMeta) =>
      Boolean(meta.uuid) && meta.offset < meta.total && !meta.loading,
    fn: (meta: DictionaryMeta) => {
      loadWordsCancel?.();
      const source = axios.CancelToken.source();
      loadWordsCancel = source.cancel;
      return {
        params: {
          uuid: meta.uuid as string,
          offset: meta.offset,
          search: meta.search,
        },
        cancelToken: source.token,
      };
    },
    target: loadWordsFx,
  });

  const searchLoadImmediate = createEvent<{
    request: LoadWordsRequest;
    search: string;
  }>();

  sample({
    clock: searchChanged,
    source: { meta: $meta, lastTime: $lastSearchTime },
    filter: (
      src: { meta: DictionaryMeta; lastTime: number },
      _search: string,
    ) => {
      if (!src.meta.uuid) return false;
      const now = Date.now();
      return (
        src.lastTime === 0 || now - src.lastTime >= SEARCH_DEBOUNCE_MS
      );
    },
    fn: (
      src: { meta: DictionaryMeta; lastTime: number },
      search: string,
    ) => {
      loadWordsCancel?.();
      checkWordCancel?.();
      const source = axios.CancelToken.source();
      loadWordsCancel = source.cancel;
      return {
        request: {
          params: {
            uuid: src.meta.uuid as string,
            offset: 0,
            search,
          },
          cancelToken: source.token,
        },
        search,
      };
    },
    target: searchLoadImmediate,
  });

  sample({
    clock: searchLoadImmediate,
    fn: (p) => p.request,
    target: loadWordsFx,
  });

  sample({
    clock: searchLoadImmediate,
    fn: (p) => p.search,
    target: setLastSearchRequested,
  });

  sample({
    clock: searchLoadImmediate,
    fn: () => Date.now(),
    target: setLastSearchTime,
  });



  const searchDebouncedRun = createEvent<{
    request: LoadWordsRequest;
    search: string;
  }>();

  sample({
    clock: debounce({
      source: searchChanged,
      timeout: SEARCH_DEBOUNCE_MS,
    }),
    source: { meta: $meta, lastRequested: $lastSearchRequested },
    filter: (
      src: { meta: DictionaryMeta; lastRequested: string | null },
      debouncedSearch: string,
    ) => Boolean(src.meta.uuid) && debouncedSearch !== src.lastRequested,
    fn: (
      src: { meta: DictionaryMeta; lastRequested: string | null },
      debouncedSearch: string,
    ) => {
      loadWordsCancel?.();
      checkWordCancel?.();
      const source = axios.CancelToken.source();
      loadWordsCancel = source.cancel;
      return {
        request: {
          params: {
            uuid: src.meta.uuid as string,
            offset: 0,
            search: debouncedSearch,
          },
          cancelToken: source.token,
        },
        search: debouncedSearch,
      };
    },
    target: searchDebouncedRun,
  });

  sample({
    clock: searchDebouncedRun,
    fn: (p) => p.request,
    target: loadWordsFx,
  });

  sample({
    clock: searchDebouncedRun,
    fn: (p) => p.search,
    target: setLastSearchRequested,
  });

  sample({
    clock: searchDebouncedRun,
    fn: () => Date.now(),
    target: setLastSearchTime,
  });

  reset.watch(() => {
    loadWordsCancel?.();
    checkWordCancel?.();
    loadWordsCancel = null;
    checkWordCancel = null;
  });

  return {
    $meta,
    $words,
    $knownWords,
    initDictionary,
    loadMoreRequested,
    searchChanged,
    seedKnownWords,
    deleteWordFx,
    addWord: addWordRequested,
    addWordDone: addWordFx.doneData,
    checkWord: checkWordRequested,
    reset,
    $knownWordsOnlyDictionary
  };
};

export const agencyDictionary = createDictionaryModel({
  name: 'desk.agencyName',
  initialWords: AGENCY_NAME_OPTIONS,
});

export const clientDictionary = createDictionaryModel({
  name: 'desk.clientName',
  initialWords: CLIENT_NAME_OPTIONS,
});

